import React, { useState, useEffect } from 'react';
import { Input, Button, Table, Tag, Icon } from 'antd';
import { Typography } from 'antd';
import copy from 'clipboard-copy';
import download from 'downloadjs/download';
import cookies from 'js-cookie';

import MainMenu from 'components/mainMenu';
import requestAPI from 'helpers/requestAPI';
import { toastError, toastSuccess, toastWarning } from 'utils/toastr';

import './domainsChecker.scss';

const DomainsChecker = () => {
	const [ query, setQuery ] = useState('');
	const [ checkerData, setCheckerData ] = useState([]);
	const [ loadingTable, setLoadingTable ] = useState(false);
	const [ selectedRowId, setSelectedRowId ] = useState([]);
	let [ refreshAfterSeconds, updateRefreshAfter ] = useState(30);
	const [ pagination, setPagination ] = useState({});
	const [ status, setStatus ] = useState('Finished');
	const [ downloadDisalbe, setDownloadDisable ] = useState(false)

	const { Title } = Typography;
	const { TextArea } = Input;

	let autoUpdateTableIntervalId = null;
	let activeProcess = cookies.get('domain-checker_started');

	useEffect(
		() => {
			let timer = 30;
			if (activeProcess) {
				activeProcessDone();
				// eslint-disable-next-line
				autoUpdateTableIntervalId = setInterval(() => {
					if (timer === 0) {
						timer = 30;
						activeProcessDone();
					} else {
						timer--;
					}
					updateRefreshAfter(timer);
				}, 1000);

				return () => clearInterval(autoUpdateTableIntervalId);
			}
		},
		[ activeProcess ]
	);

	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			width: '60%'
		},
		{
			title: 'Premium',
			dataIndex: 'premium',
			key: 'premium',
			render: (value) => {
				if (value !== '') {
					return value ? <Tag color="green">{`${value}`}</Tag> : <Tag color="red">{`${value}`}</Tag>;
				}
			}
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (value) => {
				if (value !== '') {
					return value ? <Tag color="green">{`${value}`}</Tag> : <Tag color="red">{`${value}`}</Tag>;
				}
			}
		}
	];

	const dataAdapter = (data) => {
		return data.map((elem, index) => ({
				id: elem.id,
				key: index,
				name: elem.domain,
				premium: JSON.parse(elem.data.is_premium),
				status: elem.data.available
		}))
	}

	const activeProcessDone = async (page = 0) => {
		setLoadingTable(true);

		const { status } = JSON.parse(activeProcess)

		const params2 = {
			routeParams: {
				route: `v1/domains/search/${JSON.parse(activeProcess).uid}`
			},
			anotherApi: true,
			method: 'GET',
			additionalQueryStringParams: {
				take: 10,
				skip: page * 10
			},
		};

		const paramsCheckStatus = {
			routeParams: {
				route: `v1/tasks/${JSON.parse(activeProcess).task_id}/state`
			},
			anotherApi: true,
			method: 'GET',
		};

		await requestAPI(params2)
			.then((resp) => {
					setPagination({
						...pagination,
						current: page + 1,
						pageSize: 10,
						total: resp.data.total
					})

					const dataChecker = dataAdapter(resp.data.data)

					setCheckerData(dataChecker);
			})
			.catch((error) => {
				if (error.response.data && error.response.data.detail) {
					toastError(error.response.data.detail)
					return
				}

				toastError(error)
			});

		await requestAPI(paramsCheckStatus)
			.then((resp) => {
				if (resp.data.state === 'finished' || status) {
					setStatus('Finished')
					cookies.set(
						'domain-checker_started',
						JSON.stringify({
							uid: JSON.parse(activeProcess).uid,
							task_id: JSON.parse(activeProcess).task_id,
							status: true
						}), {expires: 100 }
					)

					if (!!autoUpdateTableIntervalId) clearInterval(autoUpdateTableIntervalId);

					updateRefreshAfter(30)

					return
				}

				setStatus('In progress')
			}).catch(error => {
				if (error.response && error.response.data.detail) {
					toastError(error.response.data.detail)
					return
				}
				toastError(error)
			});

		setLoadingTable(false);
	};

	const onClickStart = async () => {
		if (!query) {
			toastError('Please enter query');
			return;
		}

		if (activeProcess) {
			if (!JSON.parse(activeProcess).status) {
				toastError('Domain checker has already started!');
				return;
			} else {
				cookies.set('domain-checker_started', '', { expires: 0 });
			}
		}

		setPagination({
			page: 1,
			pageSize: 10,
			total: 10
		})

		const params = {
			routeParams: {
				route: 'v1/domains/search'
			},
			method: 'POST',
			anotherApi: true,
			data: {
				domains: query.split('\n')
			}
		};

		await requestAPI(params)
			.then((resp) => {
				if (resp.data.error) {
					toastError(resp.data.error);
				} else {
					cookies.set(
						'domain-checker_started',
						JSON.stringify({
							uid: resp.data.job_id,
							task_id: resp.data.task_id,
							status: false
						}), {expires: 100 }
					);

					toastSuccess('Domain checker process started!');

					setQuery('');
				}
			})
			.catch((error) => {
				if (error.response.data.detail) {
					toastError(error.response.data.detail)
					return
				}

				toastError(error)
			});
	};

	const onClickStop = () => {
		if (!activeProcess) {
			toastError('Nothing to stop!');
		}

		if (activeProcess) {
			if (JSON.parse(activeProcess).status) {
				toastError('Checker already finished work!');
				return;
			}
		}

		const { uid, task_id } = JSON.parse(activeProcess)
		const params = {
			routeParams: {
				route: `v1/tasks/${task_id}`
			},
			anotherApi: true,
			method: 'DELETE',
		};

		requestAPI(params)
			.then(() => {
				toastSuccess('Process stoped');

				cookies.set(
					'domain-checker_started',
					JSON.stringify({
						uid,
						task_id,
						status: true
					}), {expires: 100 }
				)
			})
			.catch((err) => {
				toastError(err);
			});
	};

	const selectRow = (record, e) => {
		if (typeof record.key !== 'string') {
			if (e.ctrlKey || e.metaKey) {
				const checkElement = selectedRowId.indexOf(record.key);
				if (checkElement >= 0) {
					setSelectedRowId(
						selectedRowId.filter((elem) => elem !== record.key)
					);
				} else {
					setSelectedRowId([ ...selectedRowId, record.key ]);
				}
			} else if (e.shiftKey) {
				if (selectedRowId.length) {
					const newSelectedIdArr = checkerData
						.filter((elem) => {
							if (selectedRowId[0] > record.key) {
								return elem.key <= selectedRowId[0] && elem.key >= record.key;
							} else {
								return elem.key >= selectedRowId[0] && elem.key <= record.key;
							}
						})
						.map((elem) => elem.key);

					setSelectedRowId(newSelectedIdArr);
				}
			} else {
				setSelectedRowId([ record.key ]);
			}
		}
	};

	const rowSelection = {
		selectedRowKeys: selectedRowId,
		columnWidth: '0px',
		onChange: (selectedRowKeys) => {
			setSelectedRowId(selectedRowKeys);
		},
		getCheckboxProps: (record) => ({
			disabled: typeof record.key === 'string'
		})
	};

	const getDomainsById = () => {
		const domains = checkerData
			.filter(elem => selectedRowId.indexOf(elem.key) >= 0)
			.map(elem => elem.name);

		return domains;
	};

	const handleCopyPress = (e) => {
		if (
			(e.ctrlKey && e.key === 'c') ||
			(e.metaKey && e.key === 'c') ||
			(e.ctrlKey && e.key === 'с') ||
			(e.metaKey && e.key === 'с')
		) {
			if (!selectedRowId.length) {
				toastWarning('Nothing to copy');
				return;
			}

			copy(getDomainsById().join('\n'));
			toastSuccess('Domains copied!');
		}
	};

	const handleDownloadFileWithSelectedDomains = () => {
		if (!selectedRowId.length) {
			toastWarning('Nothing to download');
			return;
		}
		download(getDomainsById().join('\n'), 'domains.txt', 'text/plain');
	};

	const handleDownloadFileWithAllDomains = async () => {
		if (!activeProcess) {
			toastWarning('Nothing to download')
			return
		}
		const params = {
			routeParams: {
				route: `v1/domains/search/${JSON.parse(activeProcess).uid}`
			},
			anotherApi: true,
			method: 'GET',
			additionalQueryStringParams: {
				take: pagination.total,
				skip: 0
			},
		};

		setDownloadDisable(true)

		let downloadingData = [];

		await requestAPI(params).then(response => {
			downloadingData = dataAdapter(response.data.data)
		}).catch(error => {
			if (error.response.status === 425) {
				downloadingData = dataAdapter(error.response.data.data)
			}
		})

		if (!downloadingData.length) {
			toastWarning('Nothing to download');
			return;
		}

		const downloadingDomains = downloadingData
			.map(elem => elem.name)
			.join('\n');

		download(downloadingDomains, 'domains.txt', 'text/plain');

		setDownloadDisable(false)
	};

	const onChangePagination = (pagination) => {
		setPagination({...pagination});
		activeProcessDone(pagination.current - 1)
	}

	const handleChangeTextArea = (e) => {
		setQuery(e.target.value)
	};

	const refreshTable = () => {
		if(!activeProcess) return;
		activeProcessDone();
	};

	return (
		<div style={{ height: 'calc(100% - 48px)' }}>
			<MainMenu />

			<div className="domains-checker">
				<Title className="domains-checker__title">Domains Checker</Title>
				<p className="domains-checker__title__description">Тут можешь проверить домен\список доменов на возможность покупки</p>
				<div className="domains-checker__functional-area">
					<div className="domains-checker__functional-area__left-side">
						<TextArea
							value={query}
							onChange={handleChangeTextArea}
							rows={15}
							placeholder="Add sites to check..."
						/>
						<div className="domains-checker__functional-area__left-side__buttons">
							<Button
								className="domains-checker__functional-area__left-side__buttons__start"
								type="primary"
								onClick={onClickStart}
							>
								Start
							</Button>
							 <Button type="danger" onClick={onClickStop}>
								Stop
							</Button>
						</div>
					</div>
					<div className="domains-checker__functional-area__right-side">
						<div onKeyDown={handleCopyPress} className="domains-checker__functional-area__right-side__wrapper">
							{activeProcess &&
								<p>Job_id for admin: {JSON.parse(activeProcess).uid}</p>
							}
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<span>Information:</span>
								<span>Status: <span className={`info-status ${status === 'Finished' ? 'done' : ''}`}>{status}</span></span>
								<span>Refresh after: {refreshAfterSeconds}</span>
								<Icon type="reload" style = {{cursor: 'pointer'}} onClick={refreshTable} />
							</div>
							<Table
								rowSelection={rowSelection}
								columns={columns}
								dataSource={checkerData}
								size={'small'}
								loading={loadingTable}
								scroll={{ y: '264px' }}
								onRow={(record) => ({
									onClick: (e) => {
										selectRow(record, e);
									}
								})}
								pagination={pagination}
								onChange={Object.keys(pagination).length ? onChangePagination : null}
							/>
							<div className="domains-checker__functional-area__right-side__buttons">
								<Button type="primary" onClick={handleDownloadFileWithAllDomains} disabled={downloadDisalbe}>
									Download all
								</Button>
								<Button type="dashed" onClick={handleDownloadFileWithSelectedDomains} disabled={downloadDisalbe}>
									Download selected
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DomainsChecker;
